import LoadingImg from '@/assets/icons/loading-button.svg';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Web3DefaiProgramInteraction } from '@/program/web3DefaiLaunchpad';
import { toPublicKey, updateStrategyConfigByCoin } from '@/utils/util';
import { useGetCoinInfoState } from '@/zustand-store/coin/selector';
import { useWallet } from '@solana/wallet-adapter-react';
import base58 from 'bs58';
import React, { useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { twMerge } from 'tailwind-merge';
import { errorAlert, successAlert } from '../others/ToastGroup';
import { STRATEGY_ENUM, STRATEGY_LIST } from './config';
import { AddIcon, ArrowDownIcon, DeleteIcon } from './createIcons';

interface ModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSuccess: () => void;
}

const SetupDcaConfigModal: React.FC<ModalProps> = ({
  isOpen,
  closeModal,
  onSuccess,
}) => {
  const wallet = useWallet();
  const [errors, setErrors] = useState({
    interval: '',
  });
  const coin = useGetCoinInfoState('coin');
  const curveInfo = useGetCoinInfoState('curveInfo');
  const [selectStrategy, setSelectStrategy] = useState(false);
  const refDCA = useRef();
  const [selectedConfig, setSelectedConfig] = useState(STRATEGY_LIST[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [customStrategy, setCustomStrategy] = useState<string>();

  const [dcaConfig, setDcaConfig] = useState([
    {
      id: 1,
      token: null,
      interval: null,
      volume: null,
      minTokenPrice: 0,
      maxTokenPrice: 0,
    },
  ]);

  useOnClickOutside(refDCA, () => {
    setSelectStrategy(false);
  });

  const handleChangeDCAConfig = (id, key, val) => {
    setDcaConfig((data) => {
      const newData = data.reduce((acc, cur) => {
        if (cur.id === id) {
          acc = [
            ...acc,
            {
              ...cur,
              [key]: val || '',
            },
          ];
        } else {
          acc = [...acc, cur];
        }
        return acc;
      }, []);

      return newData;
    });
  };

  const validateFormDCA = () => {
    const curveMin =
      import.meta.env.VITE_APP_SOLANA_ENV === 'mainnet-beta' ? 300 : 0;
    const partyRoundTimeMin =
      import.meta.env.VITE_APP_SOLANA_ENV === 'mainnet-beta' ? 1 : 0;

    const validationErrors = {
      interval: dcaConfig.some(
        (config) =>
          config.interval >= (curveInfo?.['fundsExpiration'] || 0) / 86400,
      )
        ? 'Interval day must be less than fund expiration time.'
        : '',
    };

    setErrors((e) => {
      return {
        ...e,
        ...validationErrors,
      };
    });

    const errorMessages = Object.values(validationErrors).filter(Boolean);
    if (errorMessages.length > 0) {
      errorMessages.forEach((message) => errorAlert(message));
      return false;
    }

    return true;
  };

  const updateStrategyConfig = async () => {
    try {
      setIsLoading(true);
      if (!customStrategy) {
        errorAlert('Custom Strategy Required.');
      }

      const textMsg = `Custom setup strategy.`;
      const msg = new TextEncoder().encode(textMsg);

      const sig = await wallet.signMessage?.(msg);
      const signature = base58.encode(sig as Uint8Array);

      const updateCoinData = await updateStrategyConfigByCoin(coin.token, {
        customStrategy,
        signature,
        msg: textMsg,
      });

      if (!updateCoinData) {
        errorAlert('Error Setup Strategy Config.');
        setIsLoading(false);
        return;
      }
      successAlert('Set up config successfully!');
      onSuccess();
      closeModal();
    } catch (error) {
      errorAlert('An unexpected error occurred.');
      console.error('updateStrategyConfig', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createPolicyForCoin = async () => {
    if (!validateFormDCA()) {
      errorAlert('Please fix the errors before submitting.');
      return;
    }

    try {
      setIsLoading(true);

      console.log('dcaConfig--->', dcaConfig);
      const web3Defai = new Web3DefaiProgramInteraction();
      const res = await web3Defai.submitPolicy(
        wallet,
        { coin, dcaConfig: (dcaConfig || []) as any } as any,
        toPublicKey(coin.token),
      );

      if (res === 'WalletError' || !res) {
        errorAlert('Payment failed or was rejected.');
        setIsLoading(false);
        return;
      }
      if (res === 'MissingConfig') {
        errorAlert('Missing Strategy Config.');
        setIsLoading(false);
        return;
      }

      successAlert('Set up config successfully!');
      onSuccess();
      closeModal();
    } catch (error) {
      errorAlert('An unexpected error occurred.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={twMerge(
        'fixed w-full inset-0 flex items-center justify-center z-50 backdrop-blur-md',
        !isOpen && 'hidden',
      )}
    >
      <div className="flex w-full max-w-[480px] sm:max-w-2xl flex-col p-6 rounded-lg gap-3 bg-[#13141D] relative">
        <div className="">
          <h2 className="text-[18px] font-medium text-center mb-6">
            Setup Configuration
          </h2>
          <button
            onClick={() => closeModal()}
            className="absolute top-6 right-6 text-gray-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                d="M13.5909 12.5L18.0441 8.04687C18.2554 7.8359 18.3743 7.54962 18.3745 7.25099C18.3748 6.95237 18.2564 6.66587 18.0455 6.45453C17.8345 6.24319 17.5482 6.12431 17.2496 6.12404C16.951 6.12378 16.6645 6.24215 16.4531 6.45312L12 10.9062L7.54687 6.45312C7.33553 6.24178 7.04888 6.12305 6.75 6.12305C6.45111 6.12305 6.16447 6.24178 5.95312 6.45312C5.74178 6.66447 5.62305 6.95111 5.62305 7.25C5.62305 7.54888 5.74178 7.83553 5.95312 8.04687L10.4062 12.5L5.95312 16.9531C5.74178 17.1645 5.62305 17.4511 5.62305 17.75C5.62305 18.0489 5.74178 18.3355 5.95312 18.5469C6.16447 18.7582 6.45111 18.8769 6.75 18.8769C7.04888 18.8769 7.33553 18.7582 7.54687 18.5469L12 14.0937L16.4531 18.5469C16.6645 18.7582 16.9511 18.8769 17.25 18.8769C17.5489 18.8769 17.8355 18.7582 18.0469 18.5469C18.2582 18.3355 18.3769 18.0489 18.3769 17.75C18.3769 17.4511 18.2582 17.1645 18.0469 16.9531L13.5909 12.5Z"
                fill="#9192A0"
              />
            </svg>
          </button>
          <div className="flex flex-col gap-6">
            <div>
              <label className="uppercase text-[12px] font-medium text-[#84869A] flex items-center cursor-pointer">
                Investment Strategy&nbsp;
                <span className="text-red-700">*</span>
              </label>

              <div
                ref={refDCA}
                className="relative cursor-pointer mt-3 z-10"
                onClick={() => {
                  setSelectStrategy(!selectStrategy);
                }}
              >
                <div className="flex items-center justify-between w-full px-3 border border-[#585A6B] rounded h-12">
                  <div className="flex items-center">
                    <p
                      className={twMerge(
                        'text-[#E8E9EE]',
                        !selectedConfig.isValid && 'brightness-75',
                      )}
                    >
                      {selectedConfig.label}
                      {/* &nbsp;
                              {selectedConfig.desc} */}
                    </p>
                  </div>
                  <ArrowDownIcon />
                </div>
                {selectStrategy && (
                  <div className="pt-2 absolute top-full w-full">
                    <div className="bg-[#1A1C28] shadow shadow-[rgba(0,_0,_0,_0.10)] p-3 text-[12px] text-[#F7F7F7] rounded-lg flex flex-col gap-2 overflow-y-auto h-100%">
                      {STRATEGY_LIST.map((e, ind) => {
                        return (
                          <button
                            disabled={!e.isValid}
                            className={twMerge(
                              'flex items-center justify-between rounded-lg hover:bg-[#13141D] p-3 disabled:cursor-not-allowed',
                              e.isValid && 'hover:bg-[#13141D]',
                              !e.isValid && 'hover:bg-transparent',
                              selectedConfig.value === e.value &&
                                'bg-[#13141D]',
                            )}
                            onClick={() => {
                              setSelectedConfig(e);
                            }}
                            key={`agent-item-${ind}`}
                          >
                            <div className="text-left">
                              <p
                                className={twMerge(
                                  'text-[#E8E9EE]',
                                  !e.isValid && 'brightness-75',
                                )}
                              >
                                {e.label}
                              </p>
                              <p className="text-[#585A6B] text-[12px]">
                                {e.desc}
                              </p>
                            </div>
                            <>
                              {!e.isValid && (
                                <div className="bg-[rgba(159,_244,_207,_0.08)] rounded-[2px] uppercase flex items-center text-[#9FF4CF] text-[12px] font-medium p-[6px]">
                                  coming soon
                                </div>
                              )}
                            </>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {errors.interval && (
              <div className="text-[red] text-[12px] ml-auto font-bold italic">
                {errors.interval}
              </div>
            )}
            <div>
              <label
                htmlFor="name"
                className="uppercase text-[12px] font-medium text-[#84869A] flex items-center cursor-pointer"
              >
                Config&nbsp;
                <span className="text-red-700">*</span>
              </label>
              {selectedConfig.value === STRATEGY_ENUM.DCA ? (
                <>
                  {dcaConfig.map((e, idx) => {
                    return (
                      <div
                        className="flex items-center gap-2 md:flex-nowrap flex-wrap"
                        key={`idn-config-${idx}-${e.id}--`}
                      >
                        <input
                          placeholder="Token CA..."
                          role="presentation"
                          autoComplete="off"
                          id="ticker"
                          type="text"
                          value={e.token || ''}
                          onChange={(event) => {
                            handleChangeDCAConfig(
                              e.id,
                              'token',
                              event.target?.value,
                            );
                          }}
                          className={twMerge(
                            `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`,
                          )}
                        />
                        <NumberFormat
                          placeholder={`Interval (by day)...`}
                          thousandSeparator
                          className={`outline-none focus:outline-none w-full px-3 border ${
                            errors.interval
                              ? 'border-[red]'
                              : 'border-[#585A6B]'
                          } mt-3 rounded h-12 text-[#E8E9EE] bg-transparent`}
                          decimalScale={1}
                          type="text"
                          value={e.interval}
                          onChange={() => {}}
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            // allow !floatValue to let user can clear their input
                            return !floatValue || floatValue >= 0;
                          }}
                          onValueChange={({ floatValue }) => {
                            handleChangeDCAConfig(e.id, 'interval', floatValue);
                          }}
                        />
                        {dcaConfig.length > 1 && (
                          <button
                            className=""
                            onClick={() => {
                              console.log('e.id, x.id', e.id, dcaConfig);
                              setDcaConfig((data) => {
                                return data.filter((x) => x.id !== e.id);
                              });
                            }}
                          >
                            <DeleteIcon />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                <textarea
                  id="customStrategy"
                  value={customStrategy || ''}
                  onChange={(e) => {
                    setCustomStrategy(e.target.value);
                  }}
                  placeholder="Custom by user..."
                  rows={4}
                  className={twMerge(
                    `outline-none focus:outline-none w-full px-3 border border-[#585A6B] mt-3 rounded text-[#E8E9EE] bg-transparent`,
                    // errors.description && "border-red-700"
                  )}
                />
              )}
            </div>
            {selectedConfig.value === STRATEGY_ENUM.DCA &&
              dcaConfig?.length < 5 && (
                <div className="flex items-center justify-center ">
                  <button
                    onClick={() => {
                      setDcaConfig((data) => {
                        const newID = Date.now();
                        return [
                          ...data,
                          {
                            id: newID,
                            token: null,
                            interval: null,
                            volume: null,
                            maxTokenPrice: 0,
                            minTokenPrice: 0,
                          },
                        ];
                      });
                    }}
                    className="flex text-[#E8E9EE] hover:underline"
                  >
                    <AddIcon />
                    &nbsp; Add Token
                  </button>
                </div>
              )}
            <button
              disabled={isLoading || !wallet.publicKey}
              onClick={
                selectedConfig.value === STRATEGY_ENUM.DCA
                  ? createPolicyForCoin
                  : updateStrategyConfig
              }
              className="disabled:opacity-75 disabled:cursor-not-allowed uppercase p-1 rounded border-[2px] border-solid border-[rgba(255,255,255,0.25)] cursor-pointer hover:border-[rgba(255,255,255)] transition-all ease-in duration-150"
            >
              <div className="uppercase rounded bg-white px-6 py-2 text-[#080A14] flex items-center justify-center w-full">
                {isLoading && <img src={LoadingImg} />}&nbsp;Setup Strategy
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupDcaConfigModal;
